import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded"
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded"
import RuleRoundedIcon from "@mui/icons-material/RuleRounded"
import { Stack, Typography } from "@mui/material"
import { ReactElement, useState } from "react"

import DashboardPanelFrame from "../../components/dashboard/dashboardPanelFrame"
import ListDevices from "../../components/dashboard/listDevices"
import SingleKpiCard from "../../components/dashboard/singleKpiCard"
import ErrorDisplay from "../../components/fallback/errorDisplay"
import DashboardHomepageSkeleton from "../../components/loading/skeletons/dashboardHomepageSkeleton"
import ReleaseSnackbar from "../../components/snackbar/releaseSnackbar"
import { DB_ASSESSOR_DEMAND, DISPLAY_ROLES } from "../../constants/roles"
import {
    TT_DEVICES_ALERT,
    TT_DEVICES_AWAITING_ASSESSMENT,
} from "../../constants/tooltips"
import { useGetUserData } from "../../hooks/useGetUserData"
import {
    DEM_AWAITING_ASSESSMENT_DEVICES,
    DEM_NON_COMPLIANT_DEVICES,
} from "../../router/routes"
import { useGetValidatorHomepageData } from "../../services/middleware/hooks/validatorInterface/useGetValidatorHomepageData"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

/**
 * Renders the dashboard for Demand Assessor users which is displayed as their homepage
 *
 */
const DemandAssessorHomepage = (): ReactElement => {
    const { firstName } = useGetUserData()
    const { data, error, isLoading } = useGetValidatorHomepageData("DEM")
    const [open, setOpen] = useState(false)

    if (isLoading) {
        return <DashboardHomepageSkeleton />
    }
    if (error) {
        return <ErrorDisplay error={error} />
    }
    if (!data) {
        return <Typography> KPI Currently Unavailable</Typography>
    }

    return (
        <Stack
            direction={{ xs: "column", md: "column" }}
            spacing={GRID_SPACING}
            alignItems="stretch"
        >
            <Stack>
                <DashboardPanelFrame
                    userName={firstName}
                    permission={DISPLAY_ROLES[DB_ASSESSOR_DEMAND]}
                />
            </Stack>

            <Stack
                direction={{ xs: "column", md: "row" }}
                gap={GRID_SPACING}
                justifyContent="center"
                marginBottom={GRID_SPACING}
            >
                <SingleKpiCard
                    Icon={HourglassTopRoundedIcon}
                    value={data.totDevicesAwaitingAssessment}
                    title="Devices Awaiting Assessment"
                    link={DEM_AWAITING_ASSESSMENT_DEVICES}
                />
                <SingleKpiCard
                    Icon={RuleRoundedIcon}
                    value={data.totDevicesNonCompliant}
                    title="Non-Compliant Devices"
                    link={DEM_NON_COMPLIANT_DEVICES}
                />
                <SingleKpiCard
                    Icon={ErrorRoundedIcon}
                    value={data.totLiveAlerts}
                    title="Devices with Alerts"
                />
            </Stack>

            <Stack
                direction={{ xs: "column", md: "row" }}
                gap={GRID_SPACING}
                justifyContent="center"
                marginBottom={GRID_SPACING}
            >
                <ListDevices
                    cardTitle="Devices Awaiting Assessment"
                    cardDescription="These are the last 20 devices that are awaiting assessment"
                    cardTooltip={TT_DEVICES_AWAITING_ASSESSMENT}
                    data={data.topAwaitingAssessmentSummary}
                />

                <ListDevices
                    cardTitle="Devices with Alerts"
                    cardDescription="These are the last 20 devices that have had alerts raised"
                    cardTooltip={TT_DEVICES_ALERT}
                    data={data.topLiveAlerts.map((alert) => ({
                        ...alert,
                        systemReference: alert.deviceSystemRef,
                    }))}
                />
            </Stack>
            <ReleaseSnackbar open={open} setOpen={setOpen} />
        </Stack>
    )
}

export default DemandAssessorHomepage
